// Shared non-working day helper — used by Daily Log, Schedule, and any calendar views
import { type ScheduleSettings, type HolidayInfo, toISODate } from './scheduleUtils';

export interface NonWorkingDayResult {
  isNonWorking: boolean;
  label: string | null; // e.g. 'Sunday', 'Public holiday', 'Non-workday'
}

/**
 * Determine whether a given date is a non-working day for a project.
 * Checks in order:
 * 1. Sunday
 * 2. Not in project's working_days array
 * 3. Public holiday (country-level)
 * 4. Project-specific holiday
 */
export function isProjectNonWorkingDay(
  date: Date,
  settings?: ScheduleSettings | null,
  holidays?: HolidayInfo[],
  projectHolidays?: HolidayInfo[]
): NonWorkingDayResult {
  const iso = toISODate(date);
  const dow = date.getDay();

  // 1. Sunday
  if (dow === 0) {
    return { isNonWorking: true, label: 'Sunday' };
  }

  // 2. Working days list from schedule settings
  if (settings && settings.working_days && settings.working_days.length > 0) {
    if (!settings.working_days.includes(dow)) {
      return { isNonWorking: true, label: 'Non-workday' };
    }
  } else if (settings) {
    // Fallback from old sunday_off / saturday_off fields
    if (settings.sunday_off && dow === 0) {
      return { isNonWorking: true, label: 'Sunday' };
    }
    if (settings.saturday_off && dow === 6) {
      return { isNonWorking: true, label: 'Saturday' };
    }
  }

  // 3. Public holiday (country-level)
  if (settings?.exclude_public_holidays && holidays && holidays.length > 0) {
    const holiday = holidays.find((h) => h.date === iso);
    if (holiday) {
      return { isNonWorking: true, label: holiday.name || 'Public holiday' };
    }
  }

  // 4. Project-specific holiday
  if (projectHolidays && projectHolidays.length > 0) {
    const holiday = projectHolidays.find((h) => h.date === iso);
    if (holiday) {
      return { isNonWorking: true, label: holiday.name || 'Project off-day' };
    }
  }

  return { isNonWorking: false, label: null };
}

/**
 * Simple version: just boolean, no label.
 */
export function isNonWorkingDay(
  date: Date,
  settings?: ScheduleSettings | null,
  holidays?: HolidayInfo[],
  projectHolidays?: HolidayInfo[]
): boolean {
  return isProjectNonWorkingDay(date, settings, holidays, projectHolidays).isNonWorking;
}